// What each role may do (QA #84, PM decision 2026-09-30: a read-only
// explanation page, no editable permissions). Every row mirrors a backend
// route guard (`RequireRole` in internal/*/routes.go) plus the row scoping in
// internal/rbac (admin = everything, sub_admin = their groups, operator =
// their own records) and the extra sub_admin limits in internal/users. The
// backend stays the boundary; this table only explains it. When a guard
// changes, change the row here in the same PR.
import type { UserRole } from '../types/enums.generated';
import { STORAGE_POOLS_UI } from './features';

/**
 * - all:   every record
 * - group: records in the caller's groups (plus their own ungrouped ones)
 * - own:   the caller's own records only
 * - none:  not allowed
 * A string is a short qualified answer shown as-is.
 */
export type Access = 'all' | 'group' | 'own' | 'none' | { note: string };

export interface PermissionRow {
  action: string;
  access: Record<UserRole, Access>;
}

export interface PermissionSection {
  title: string;
  rows: PermissionRow[];
}

const scoped: Record<UserRole, Access> = { admin: 'all', sub_admin: 'group', operator: 'own' };
const managersOnly: Record<UserRole, Access> = { admin: 'all', sub_admin: 'group', operator: 'none' };
const adminOnly: Record<UserRole, Access> = { admin: 'all', sub_admin: 'none', operator: 'none' };
const everyoneSelf: Record<UserRole, Access> = { admin: 'own', sub_admin: 'own', operator: 'own' };

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: '最高管理員',
  sub_admin: '群組管理員',
  operator: '操作員',
};

export const ROLE_SUMMARIES: Record<UserRole, string> = {
  admin: '管理整個系統：所有群組、所有使用者與所有資料，以及群組、儲存池、密碼有效期限等全域設定。',
  sub_admin: '管理自己所屬的群組：群組內的檔案、備份記錄、排程、備份目的地、File Watcher 與操作員帳號；不能管理其他群組，也不能建立或修改管理員。',
  operator: '使用備份功能：上傳、下載、刪除與還原自己的檔案，查看自己的備份記錄與稽核紀錄；不能進入任何管理頁面。',
};

export const ACCESS_LABELS: Record<Exclude<Access, { note: string }>, string> = {
  all: '全部',
  group: '限所屬群組',
  own: '限自己',
  none: '無',
};

export const PERMISSION_SECTIONS: readonly PermissionSection[] = [
  {
    title: '備份檔案',
    rows: [
      { action: '查看檔案清單、檔案詳情', access: scoped },
      { action: '上傳檔案', access: scoped },
      { action: '下載檔案', access: scoped },
      { action: '刪除檔案、回收桶還原與永久刪除', access: scoped },
      { action: '查看資料損壞的檔案', access: scoped },
    ],
  },
  {
    title: '備份記錄與排程',
    rows: [
      { action: '查看備份記錄', access: scoped },
      { action: '取消執行中的備份、清除失敗記錄', access: managersOnly },
      { action: '新增、編輯、刪除、啟用／停用自動備份排程', access: managersOnly },
      { action: '立即執行排程', access: managersOnly },
    ],
  },
  {
    title: '還原',
    rows: [
      { action: '設定預設還原目錄', access: managersOnly },
      { action: '一鍵還原到 File Watcher 主機', access: managersOnly },
      { action: '查看還原歷史', access: scoped },
    ],
  },
  {
    title: '儲存與來源',
    rows: [
      { action: '查看備份目的地', access: managersOnly },
      { action: '新增、編輯、刪除、啟用／停用地端目的地，測試連線', access: managersOnly },
      ...(STORAGE_POOLS_UI
        ? [
          { action: '查看儲存池', access: { admin: 'all', sub_admin: 'all', operator: 'none' } as Record<UserRole, Access> },
          { action: '新增、編輯、刪除儲存池與池內目的地', access: adminOnly },
        ]
        : []),
      { action: '新增、編輯、刪除、啟用／停用 File Watcher，重新產生金鑰、下載安裝包', access: managersOnly },
    ],
  },
  {
    title: '帳號與權限',
    rows: [
      { action: '查看使用者', access: { admin: 'all', sub_admin: 'group', operator: 'none' } },
      {
        action: '新增使用者',
        access: { admin: 'all', sub_admin: { note: '只能建立操作員，且只能加入自己的群組' }, operator: 'none' },
      },
      {
        action: '編輯、停用使用者，寄送設定密碼信，要求下次登入更換密碼',
        access: { admin: 'all', sub_admin: { note: '只限自己群組內的操作員' }, operator: 'none' },
      },
      { action: '永久刪除使用者', access: adminOnly },
      { action: '新增、編輯、刪除群組，管理群組成員', access: adminOnly },
      { action: '查看密碼有效期限', access: { admin: 'all', sub_admin: 'all', operator: 'none' } },
      { action: '調整密碼有效期限', access: adminOnly },
    ],
  },
  {
    title: '系統',
    rows: [
      { action: '查看總覽統計', access: scoped },
      { action: '查看稽核紀錄', access: scoped },
      { action: '設定告警收件人與事件、寄送測試信', access: managersOnly },
      { action: '修改自己的密碼、個人資料，啟用／停用兩步驟驗證', access: everyoneSelf },
    ],
  },
];
